import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "../utils/motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollAnimation from "../components/ScrollAnimation";
import { COUPLES } from "../constants/couples";
import { HERO, ABOUT, QUOTE, SECTION, img } from "../constants/copy";
import { FILMSDASHBOARD, videoSrc } from "../constants/videos";
import { getClientImages, getCoverImages, getClientEvents } from "../utils/githubService";
import { preloadImagesWithPriority } from "../utils/imagePreloader";
import LightboxModal from "../components/LightboxModal";
import VideoModal from "../components/VideoModal";
import "./desktopHome.css"; // styles for desktop home (theme + helper classes)
import axios from "axios";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";


/* Hero images / client folders kept from your original file */
const HERO_IMAGES = [
"/a9.jpg", "/d6.jpg", "/d11.png",
  "/a27.png", "/a8.jpg", "/a7.jpg", "/a24.jpg",
];

const CLIENT_FOLDERS = {
  "ashwini-shrikant": "Ashwini & Shrikant",
  "nikita-partik": "Nikita & Partik",
  "shivangi-bhaumik": "Shivangi & Bhaumik",
};

const GITHUB_USERNAME = process.env.REACT_APP_GITHUB_USERNAME;
const REPO_NAME = process.env.REACT_APP_GITHUB_REPO;
const BRANCH = process.env.REACT_APP_GITHUB_BRANCH;
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;

const githubApi = axios.create({
  baseURL: "https://api.github.com",
  headers: {
    Authorization: `Bearer ${GITHUB_TOKEN}`,
    Accept: "application/vnd.github+json",
  },
});

export default function DesktopHome() {
  const navigate = useNavigate();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [clientPhotos, setClientPhotos] = useState({});
  const [coverImages, setCoverImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0, title: "" });
  const [videoModal, setVideoModal] = useState({ open: false, src: null, poster: null, title: "" });

  const heroRef = useRef(null);
  
  const openSingleImage = (src, title = "") => {
    setLightbox({
      open: true,
      images: [src],
      index: 0,
      title,
    });
  };
  
  function useVisitorAlert() {
    useEffect(() => {
      // prevent multiple emails per session
      if (sessionStorage.getItem("visitorAlertSent")) return;
  
      const sendAlert = async () => {
        try {
          await emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_VISITOR_TEMPLATE_ID, // new template
            {
              time: new Date().toLocaleString(),
              user_agent: navigator.userAgent,
            },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
          );
  
          sessionStorage.setItem("visitorAlertSent", "true");
          console.log("Visitor alert sent");
        } catch (error) {
          console.error("Visitor alert failed:", error);
        }
      };
  
      sendAlert();
    }, []);
  }

  // Auto slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === HERO_IMAGES.length - 1 ? 0 : prev + 1));
    }, 4200);
    return () => clearInterval(interval);
  }, []);

  
  // Hero parallax (subtle)
  const handleHeroMove = useCallback((e) => {
    const el = heroRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(-1, Math.min(1, ((e.clientX - rect.left) / rect.width - 0.5) * 2));
    const y = Math.max(-1, Math.min(1, ((e.clientY - rect.top) / rect.height - 0.5) * 2));
    el.style.setProperty("--px", `${x * 8}px`);
    el.style.setProperty("--py", `${y * -8}px`);
    el.style.setProperty("--r", `${x * 3}deg`);
  }, []);

  const clearHeroTransform = useCallback(() => {
    const el = heroRef.current;
    if (!el) return;
    el.style.setProperty("--px", "0px");
    el.style.setProperty("--py", "0px");
    el.style.setProperty("--r", "0deg");
  }, []);
  

  const [featuredCouples, setFeaturedCouples] = useState([]);
//fetch  1st 3 couples
useEffect(() => {
  const fetchFeaturedCouples = async () => {
    try {
      const folderRes = await githubApi.get(
        `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/clients`
      );

      const folders = folderRes.data
        .filter(item => item.type === "dir")
        .slice(0, 3); // ✅ first 3 only

      const data = await Promise.all(
        folders.map(async (folder) => {
          try {
            const res = await githubApi.get(
              `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/clients/${folder.name}/${folder.name}.json`
            );

            const json = JSON.parse(atob(res.data.content));

            return {
              slug: json.clientId,
              title: json.client,
              coverImage: `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/${json.coverImage}`,
            };
          } catch {
            return null;
          }
        })
      );

      setFeaturedCouples(data.filter(Boolean));
    } catch (err) {
      console.error("Failed to load featured couples", err);
    }
  };

  fetchFeaturedCouples();
}, []);


  useEffect(() => {
    const fetchClientPhotos = async () => {
      try {
        setLoading(true);
        const photos = {};

        const clientPromises = Object.entries(CLIENT_FOLDERS).map(async ([folderName, displayName]) => {
          try {
            const [directImages, events] = await Promise.all([
              getClientImages(folderName),
              getClientEvents(folderName),
            ]);

            const eventImages = await Promise.all(
              events.map(async (ev) => {
                const imgs = await getClientImages(folderName, ev.name);
                return imgs;
              })
            );

            const allImages = [...directImages, ...eventImages.flat()];

            if (allImages.length > 0) {
              const imageUrls = allImages.slice(0, 3).map((i) => i.url);
              preloadImagesWithPriority(imageUrls, 3).catch(() => null);

              return {
                folderName,
                data: {
                  displayName,
                  images: allImages, // keep full list for lightbox
                  preview: allImages.slice(0, 3),
                  totalImages: allImages.length,
                },
              };
            }
            return null;
          } catch (err) {
            console.error(`Error fetching ${folderName}`, err);
            return null;
          }
        });

        const results = await Promise.all(clientPromises);
        results.forEach((r) => {
          if (r) photos[r.folderName] = r.data;
        });

        const covers = await getCoverImages();
        setCoverImages(covers);
        if (covers.length > 0) {
          preloadImagesWithPriority(covers.slice(0, 3).map((c) => c.url), 3).catch(() => null);
        }

        setClientPhotos(photos);
      } catch (err) {
        console.error("Error fetching client photos", err);
      } finally {
        setLoading(false);
      }
    };

    fetchClientPhotos();
  }, []);

  const [heroImages, setHeroImages] = useState([]);

  //hero sliding images 
  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        const cached = sessionStorage.getItem("hero_images");
        if (cached) {
          setHeroImages(JSON.parse(cached));
          return;
        }
  
        const res = await githubApi.get(
          `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/hero`
        );
  
        const imgs = res.data
          .filter(item => item.type === "file")
          .map(item =>
            `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/hero/${item.name}`
          );
  
        setHeroImages(imgs);
        sessionStorage.setItem("hero_images", JSON.stringify(imgs));
      } catch (err) {
        console.error("Failed to load hero images", err);
      }
    };
  
    fetchHeroImages();
  }, []);
  
  //desktop backgroun image 
  const [docImage, setDocImage] = useState(null);

useEffect(() => {
  const fetchDocImage = async () => {
    try {
      const cached = sessionStorage.getItem("desktop_doc_image");
      if (cached) {
        setDocImage(cached);
        return;
      }

      const res = await githubApi.get(
        `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/desktop`
      );

      const file = res.data.find(i => i.type === "file");
      if (!file) return;

      const url = `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/desktop/${file.name}`;

      setDocImage(url);
      sessionStorage.setItem("desktop_doc_image", url);
    } catch (err) {
      console.error("Failed to load desktop image", err);
    }
  };

  fetchDocImage();
}, []);


  // modals - lock scroll
  useEffect(() => {
    const locked = lightbox.open || videoModal.open;
    document.body.style.overflow = locked ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [lightbox.open, videoModal.open]);

  // small motion variants for consistency
  const heroVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
  };

  const cardVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.03, transition: { duration: 0.25 } },
  };

  const openLightbox = (slug) => {
    const client = clientPhotos[slug];
    if (!client || !client.images || client.images.length === 0) return;
    setLightbox({ open: true, images: client.images.map((i) => i.url), index: 0, title: client.displayName });
  };

  const openVideoModal = (video, poster, title) => {
    setVideoModal({ open: true, src: videoSrc(video), poster: img(poster), title });
  };
  const [testimonials, setTestimonials] = useState([]);
const [theme, setTheme] = useState({});

useEffect(() => {
  const fetchTestimonials = async () => {
    const clientId = "ashwini-shrikant"; // dynamic later

    const base = `https://cdn.jsdelivr.net/gh/${process.env.REACT_APP_GITHUB_USERNAME}/${process.env.REACT_APP_GITHUB_REPO}@${process.env.REACT_APP_GITHUB_BRANCH}/clients/${clientId}`;

    const [reviewsRes, themeRes] = await Promise.all([
      fetch(`${base}/testimonials.json`).then(r => r.json()),
      fetch(`${base}/theme.json`).then(r => r.json()),
    ]);

    setTestimonials(reviewsRes.reviews.filter(r => r.approved));
    setTheme(themeRes);
  };

  fetchTestimonials();
}, []);


  return (
    
    <div className="bg-weddmac-dark min-h-screen overflow-hidden font-inter text-white">
      <Header />
      {/* HERO */}
      <motion.section
  ref={heroRef}
  onMouseMove={handleHeroMove}
  onMouseLeave={clearHeroTransform}
  className="relative w-full min-h-[86vh] flex flex-col items-center justify-end pb-24 md:pb-28 overflow-hidden desktop-hero"
  initial="hidden"
  animate="visible"
  variants={heroVariants}
  style={{ perspective: 1200 }}
>
  <div className="absolute inset-0 hero-frames">
    {heroImages.map((src, i) => {
      const active = i === currentSlide;
      return (
        <motion.img
          key={src}
          src={src}  
          loading="eager"                   // ✅ from GitHub
          alt={`Hero ${i + 1}`}
          fetchPriority={i === 0 ? "high" : "auto"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out transform-gpu ${
            active ? "opacity-100" : "opacity-0"
          }`}
          style={{
            transform: `translate(var(--px, 0), var(--py, 0)) rotate(var(--r, 0)) scale(${
              active ? 1 : 1.03
            })`,
          }}
          initial={false}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      );
    })}
  </div>

  {/* Decorative SVG */}
  <svg
    className="absolute left-6 bottom-6 opacity-30 w-36 h-36"
    viewBox="0 0 100 100"
    fill="none"
    aria-hidden
  >
    <defs>
      <linearGradient id="g" x1="0" x2="1">
        <stop offset="0" stopColor="#D6B46A" stopOpacity="0.6" />
        <stop offset="1" stopColor="#fff" stopOpacity="0.05" />
      </linearGradient>
    </defs>
    <circle cx="50" cy="50" r="48" stroke="url(#g)" strokeWidth="2" />
  </svg>

  <div className="absolute inset-0 bg-gradient-to-t from-weddmac-dark/85 via-weddmac-dark/20 to-transparent pointer-events-none" />

  <h1
    className="relative z-10 text-center font-inria font-light text-3xl sm:text-4xl md:text-5xl lg:text-[58px] xl:text-[64px] leading-tight max-w-[90vw] md:max-w-4xl px-4 tracking-tight drop-shadow"
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.25 }}
  >
    {HERO.tagline}
  </h1>

  <div className="relative z-10 flex gap-4 mt-6 mb-10">
    <Link
      to="/get-in-touch"
      className="inline-flex items-center gap-3 bg-weddmac-gold text-black px-5 py-3 rounded-full shadow-lg hover:scale-[1.02]"
    >
      Enquire
    </Link>
    <Link
      to="/films"
      className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-white/10 text-white/90 hover:border-weddmac-gold/40"
    >
      View Films
    </Link>
  </div>

  {/* slide dots */}
  <div className="relative z-10 flex gap-2 mb-6">
    {heroImages.map((_, i) => (
      <button
        key={i}
        onClick={() => setCurrentSlide(i)}
        aria-label={`Go to slide ${i + 1}`}
        className={`h-1 rounded-full transition-all duration-300 ${
          i === currentSlide
            ? "w-8 bg-weddmac-gold"
            : "w-1.5 bg-white/30 hover:bg-white/60"
        }`}
      />
    ))}
  </div>
</motion.section>


      
<section className="relative mx-6 md:mx-12 lg:mx-20 my-12">
  <div className="relative overflow-hidden rounded-2xl">

    {/* Background Image */}
    {docImage && (
      <img
        src={docImage}
        alt="Documentary wedding films"
       
        className="w-full h-[360px] md:h-[480px] lg:h-[560px] object-cover"
      />
    )}

    {/* Dark overlay */}
    <div className="absolute inset-0 bg-black/40" />

    {/* Text Content */}
    <div className="absolute inset-0 flex items-center justify-center px-6">
      <p className="text-white/90 text-center max-w-2xl text-sm md:text-base leading-relaxed font-light">
        Documentary wedding films and photography — calm composition in chaotic moments.
      </p>
    </div>

  </div>
</section>




      {/* ABOUT */}
      
        <section className="relative flex flex-col lg:flex-row gap-12 lg:gap-20 items-center justify-center px-6 md:px-12 lg:px-20 py-20 lg:py-28">
          <div className="group w-full max-w-md lg:max-w-[448px] overflow-hidden rounded-2xl shadow-card" whileHover={{ scale: 1.02 }}>
            <img src={img("/d4.jpg")} alt="Weddmac about"  className="w-full h-auto object-cover rounded-2xl" />
          </div>

          <div className="max-w-lg">
            <h2 className="text-weddmac-gold font-inria text-2xl md:text-3xl mb-3"></h2>
            <p className="font-bodoni text-white/95 text-lg md:text-xl leading-relaxed">
              {ABOUT.main}
            </p>
          </div>

          <motion.div className="group w-full max-w-md lg:max-w-[448px] overflow-hidden rounded-2xl shadow-card" whileHover={{ scale: 1.02 }}>
            <img src={img("/d3.jpg")} alt="Weddmac about" loading="eager" className="w-full h-auto object-cover rounded-2xl" />
          </motion.div>
        </section>
      

      {/* QUOTE */}
   
  <section className="py-8 md:py-10">
    <blockquote className="text-center max-w-4xl mx-auto px-6">
      <p className="tagline-text">
        {QUOTE}
      </p>
    </blockquote>
  </section>


{/* IMAGE SECTION */}

  <section className="relative mx-6 md:mx-12 lg:mx-20 my-12">
    <div className="relative overflow-hidden rounded-2xl bg-black">

      {/* Image */}
      <img
  src={img("/a9.jpg")}
  alt="Explore Photography"
  // loading="eager"
  onClick={() => openSingleImage(img("/a9.jpg"), "Explore Photography")}
  className="w-full h-auto max-h-[85vh] object-contain mx-auto cursor-zoom-in"
/>


      {/* Soft dark overlay */}
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />

      {/* Caption */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center px-6">
        <p className="text-[#7fd3ff] text-xs tracking-[0.25em] uppercase mb-2">
          Explore Photography
        </p>
     
      </div>

    </div>
  </section>


  <div className="flex justify-center">
  <p className="text-white/90 text-sm md:text-base font-light max-w-xl mx-auto leading-relaxed text-center">
    Your love story deserves to be remembered forever. <br />
    Get in touch with us and let’s create magic together.
  </p>
</div>

      {/* FEATURE IMAGE */}
      
        <div className="relative group overflow-hidden rounded-2xl shadow-card mx-6 md:mx-12 lg:mx-20 mb-12">
          <img src={img("/Rectangle45.png")} alt="Featured work" className="w-full h-[320px] md:h-[420px] lg:h-[480px] object-cover rounded-2xl" />
          <h2 className="absolute inset-0 flex items-center justify-center text-center text-white font-inria font-light text-2xl md:text-4xl lg:text-5xl tracking-tight px-6">{SECTION}</h2>
        </div>
  

      {/* CLIENTS */}
      
        <section className="py-14 px-6">
          <div className="max-w-6xl mx-auto">
            <h1 className="section-heading text-center mb-3">Our Clients</h1>
            <p className="section-sub text-center mb-8 max-w-2xl mx-auto">Capturing beautiful moments for couples who trust us with their special day.</p>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {[1,2,3].map((n) => <div key={n} className="rounded-2xl bg-white/[0.02] border border-white/10 h-64 animate-pulse" />)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                  {Object.entries(clientPhotos).map(([slug, { displayName, images, preview, totalImages }], i) => (
                
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => openLightbox(slug)}
                        onKeyDown={(e) => { if (e.key === "Enter") openLightbox(slug); }}
                        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] hover:border-weddmac-gold/40 hover:bg-weddmac-gold/5 transition-all duration-300 cursor-pointer"
                        whileHover={{ scale: 1.02 }}
                      >
                        {preview && preview.length > 0 ? (
                          <div className="relative">
                            <img src={preview[0].url} alt={displayName} loading="eager" className="w-full h-64 object-cover rounded-2xl" onError={(e) => { e.currentTarget.src = img("/Rectangle45.png"); }} />
                            <div className="absolute inset-0 bg-gradient-to-t from-weddmac-dark/85 via-transparent to-transparent pointer-events-none rounded-2xl" />
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                              <h3 className="text-white font-inria text-2xl">{displayName}</h3>
                              {totalImages > 1 && <p className="text-white/70 text-sm mt-1">{totalImages} photos</p>}
                            </div>
                          </div>
                        ) : (
                          <div className="p-8 text-center">
                            <h3 className="text-white font-inria text-2xl">{displayName}</h3>
                          </div>
                        )}
                      </div>
                    
                  ))}
                </div>

              
              </>
            )}
          </div>
        </section>
    

      {/* COUPLES */}
    {/* COUPLES */}

    <section className="py-16 px-6">
  <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
    {featuredCouples.map(({ slug, title, coverImage }) => (
      <div
        key={slug}
        whileHover={{ scale: 1.03 }}
        className="group relative h-[420px] rounded-2xl overflow-hidden border border-white/10 cursor-pointer"
        onClick={() => navigate(`/clients/${slug}`)}   // ✅ SAME AS ViewAllClients
      >

        {/* Cover Image */}
        <img
          src={coverImage}
          alt={title}
          
          onClick={() => openSingleImage(coverImage, title)}
          decoding="async"
          width="400"
          height="600"
          className="absolute inset-0 w-full h-full object-cover 
                     transition-all duration-500 
                     scale-105
                     group-hover:scale-110 group-hover:blur-0"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition" />

        {/* Title */}
        <div className="absolute inset-0 flex items-end justify-center pb-8 pointer-events-none">
          <p className="text-white font-inria text-2xl md:text-3xl tracking-wide">
            {title}
          </p>
        </div>

      </div>
    ))}
  </div>
</section>

{/* view all button */}


<div className="text-center">
                  <Link to="/clients" className="btn-primary inline-flex items-center gap-2">View All Clients</Link>
                </div>

{/* MEDIA & RECOGNITIONS */}

  <section className="py-20 md:py-28">
    <div className="max-w-4xl mx-auto px-6 text-center">

      {/* Small cyan line */}
      <p className="text-[#7fd3ff] text-xs tracking-[0.22em] mb-6">
        Your love story deserves to be remembered forever.<br />
        Get in touch with us and let’s create magic together.
      </p>

      {/* Main heading */}
      <h2 className="text-white font-inria font-light text-3xl md:text-4xl lg:text-5xl mb-10">
        Media and Recognitions
      </h2>

      {/* Description */}
      <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
        We are honored to be featured on leading media pages and top wedding platforms,
        highlighting our commitment to excellence and dedication to making your special day
        truly extraordinary.
        <br /><br />
        At Weddmac, we celebrate the unbreakable bond that connects and the moments that deserve
        to be shared again and again. We believe that the paramount role of a wedding photographer
        is to encapsulate the very essence of the wedding — an authentic and heartfelt representation
        of the couple. This philosophy has led us from intimate gatherings in cozy nooks to grandiose
        celebrations.
      </p>

      {/* Footer line */}
      <p className="mt-14 text-white/40 text-xs tracking-[0.25em]">
        Unforgettable Moments · Timeless Films
      </p>

    </div>
  </section>



      {/* FILMS */}
      <section
  className="
    px-4 sm:px-6 md:px-8 lg:px-12
    py-10 sm:py-14 md:py-20 lg:py-24
    min-h-screen flex items-center
  "
>
  <div className="max-w-7xl w-full mx-auto text-center">
    <h1 className="section-heading mb-4">Films</h1>

    <p className="section-sub mb-12 max-w-3xl mx-auto">
      Wedding films that capture the energy, tenderness and imperfection of real life.
    </p>

    {/* LEFT & RIGHT layout */}
    <div className="flex flex-col lg:flex-row justify-between gap-8 lg:gap-16">
      {FILMSDASHBOARD.slice(0, 2).map(
        ({ slug, title, tagline, video, poster }, index) => (
          <div
            key={slug}
            className={`
              group relative overflow-hidden rounded-3xl
              bg-white/5 border border-white/10
              hover:border-weddmac-gold/40
              shadow-card
              w-full lg:w-[48%]
              ${index === 0 ? "lg:mr-auto" : "lg:ml-auto"}
            `}
          >
            {/* VIDEO */}
            <div className="relative w-full aspect-[16/9]">
              <video
                src={video}
                poster={img(poster)}
                 loading="eager"
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-cover rounded-3xl"
              />
            </div>

            {/* Text overlay */}
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-weddmac-dark/95 via-weddmac-dark/70 to-transparent pointer-events-none">
              <h3 className="font-inria font-light text-white text-lg md:text-xl">
                {title}
              </h3>
              <p className="text-white/70 text-sm mt-1">
                {tagline}
              </p>
            </div>
          </div>
        )
      )}
    </div>
  </div>
</section>



<section className="py-20 md:py-28 testimonials-section">
  <div className="max-w-4xl mx-auto px-6 text-center">

    <p
      className="text-xs tracking-[0.22em] mb-6"
      style={{ color: theme.accent }}
    >
      Words from couples who trusted us
      <br />
      with their most important day.
    </p>

    <h2 className="text-white font-inria font-light text-3xl md:text-4xl lg:text-5xl mb-12">
      Testimonials
    </h2>

    <blockquote className="mb-12"> <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto"> “Choosing Weddmac was the best decision we made for our wedding. They didn’t just capture moments — they captured emotions. Every photograph and frame feels honest, timeless, and deeply personal.” </p> <footer className="mt-4 text-white/50 text-sm tracking-wide"> — Ashwini & Shrikant </footer> </blockquote> {/* Testimonial 2 */} <blockquote className="mb-12"> <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto"> “Nothing felt forced or staged. The team blended in so naturally that we forgot the cameras were even there. Our wedding film still gives us goosebumps every time we watch it.” </p> <footer className="mt-4 text-white/50 text-sm tracking-wide"> — Nikita & Partik </footer> </blockquote> {/* Testimonial 3 */} <blockquote> <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto"> “Weddmac has an incredible eye for detail. From quiet, emotional moments to wild celebrations, everything was captured so beautifully and effortlessly.” </p> <footer className="mt-4 text-white/50 text-sm tracking-wide"> — Shivangi & Bhaumik </footer> </blockquote>

    {testimonials.slice(0, 3).map((t, i) => (
      <blockquote key={i} className="mb-12 testimonial-card">
        <p
          className="text-base md:text-lg leading-relaxed max-w-3xl mx-auto"
          style={{ color: theme.quoteColor }}
        >
          “{t.text}”
        </p>
        <footer
          className="mt-4 text-sm tracking-wide"
          style={{ color: theme.authorColor }}
        >
          — {t.author} · ⭐ {t.rating}/5
        </footer>
      </blockquote>
    ))}

    {/* BUTTONS */}
    <div className="mt-10 flex justify-center gap-4">
      <Link to="/reviews" className="btn-ghost">
        View All Testimonials →
      </Link>
      <Link to="/add-review" className="btn-primary">
        Add Your Review
      </Link>
    </div>

    <p className="mt-16 text-white/40 text-xs tracking-[0.25em]">
      Real Stories · Honest Emotions · Timeless Memories
    </p>

  </div>
</section>




  <section className="relative mx-6 md:mx-12 lg:mx-20 my-16">
    <div className="relative overflow-hidden rounded-2xl">

      {/* Background Image */}
      <img
  src="https://www.mistay.in/travel-blog/content/images/2020/06/udaipur.jpg"
   loading="eager"
  alt="Wedding moments"
  className="w-full h-[320px] md:h-[400px] lg:h-[460px] object-cover"
/>


      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/55" />

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center px-6">
        <div className="max-w-3xl text-center">

          <h2 className="font-inria font-light text-white text-2xl md:text-3xl lg:text-4xl mb-4">
            We would love to hear from you!
          </h2>

          <p className="text-white/80 text-sm md:text-base leading-relaxed">
            Want to get to know our clients and build a genuine connection with them
            before the big day. It’s not just about taking the best photos & videos —
            it’s about how we make our clients feel. The experience from the first
            initial connection to the final deliverables, we make sure everything is
            consistent and flawless.
          </p>

        </div>
      </div>

    </div>
  </section>


      <Footer />

      {/* Lightbox and Video modals */}
      <LightboxModal
        open={lightbox.open}
        images={lightbox.images}
        index={lightbox.index}
        title={lightbox.title}
        onClose={() => setLightbox((s) => ({ ...s, open: false }))}
        onPrev={() => setLightbox((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length }))}
        onNext={() => setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length }))}
      />

      <VideoModal
        open={videoModal.open}
        src={videoModal.src}
        poster={videoModal.poster}
        title={videoModal.title}
        onClose={() => setVideoModal({ open: false, src: null, poster: null, title: "" })}
      />
    </div>
  );
}